import React from 'react';
import { bool, func, number, oneOfType, string } from 'prop-types';
import classNames from 'classnames';
import { Text } from '@fiverr-private/typography';
import { I18n, translate } from '@fiverr-private/i18n-react';
import { regulationTranslation } from '@fiverr-private/price_regulation_translations';
import { useGigListingsContext } from '../../../../GigCardListings/context/GigListingsContext';
import { trackGigForClickType } from '../../../utils';
import { priceMarkupFormatter } from '../../../../GigCardListings/ListingsTransformer/priceTransformer';
import { clickedElement } from '../../../Components/Price';
import { useCardContext } from '../../../../GigCardListings/context/CardContext';
import { useDisplayPrice } from '../../../../hooks';
import ContextualLink from '../../../Components/ContextualLink';
import TooltipWrapper from '../../../../shared/components/TooltipWrapper';
import { PriceUpfrontTooltip } from '../../../../shared/components/PriceUpfrontTooltip';
import styles from './index.module.scss';

const Price = ({
    originalPrice = 0,
    url,
    urlTarget,
    pricingFactorAlias,
    onMouseEnter,
    onMouseLeave,
    className = '',
    textClassName = '',
    priceDisplayStatus = 'none',
    auctionId = null,
    showHourlyPrice = false,
    hourlyRate,
}) => {
    const { currencyObj } = useGigListingsContext();
    const {
        enrichedTracker: { trackEvent },
    } = useCardContext();

    const regulationTranslationText = regulationTranslation(priceDisplayStatus);

    const { displayPrice: price, showTooltip } = useDisplayPrice({
        originalPrice,
        hourlyRate,
        showHourlyPrice,
        currency: currencyObj,
    });

    if (showHourlyPrice && !hourlyRate) {
        return null;
    }

    const clickedPriceElement = clickedElement(price);

    return (
        <TooltipWrapper content={<PriceUpfrontTooltip />} shouldRenderTooltip={showTooltip} position="bottom">
            <ContextualLink
                href={url}
                target={urlTarget}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                onMouseDown={(e) => trackGigForClickType(e.button, trackEvent, clickedPriceElement)}
                className={classNames('m-t-8', 'tbody-5', styles.link, className)}
                ariaLabel={translate('gig_cards.gig_link_aria_label')}
                auctionId={auctionId}
            >
                <span className={classNames('text-bold', 'co-grey-1200', textClassName)}>
                    {!showHourlyPrice && (
                        <>
                            <I18n k="gig_listings.gig_cards.price.from" />
                            &nbsp;
                        </>
                    )}
                    <span dangerouslySetInnerHTML={{ __html: priceMarkupFormatter({ currencyObj, price }) }} />
                    {showHourlyPrice && <I18n k="gig_listings.gig_cards.price.hourly.suffix" />}
                    {regulationTranslationText && (
                        <Text paddingLeft="1.5" fontWeight="normal" color="bodySecondary" as="span">
                            {regulationTranslationText}
                        </Text>
                    )}
                </span>

                {!showHourlyPrice && pricingFactorAlias && (
                    <span className={classNames('co-grey-900', styles.pricingFactor)}>
                        &nbsp;/ {pricingFactorAlias}
                    </span>
                )}
            </ContextualLink>
        </TooltipWrapper>
    );
};

Price.propTypes = {
    originalPrice: number,
    url: string,
    urlTarget: string,
    pricingFactorAlias: string,
    onMouseEnter: func,
    onMouseLeave: func,
    className: string,
    textClassName: string,
    priceDisplayStatus: string,
    auctionId: string,
    showHourlyPrice: bool,
    hourlyRate: oneOfType([string, number]),
};

export default Price;
